import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios";
import { Trophy, Crown, MapPin, Sparkles } from "lucide-react";
import { useLang } from "@/lib/lang";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Leaderboard() {
  const { lang } = useLang();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/leaderboard/top10`).then(r => setData(r.data)).finally(() => setLoading(false));
  }, []);

  const T = lang === "fr" ? FR : EN;

  return (
    <section className="relative border-t border-zinc-200 bg-gradient-to-br from-amber-50/60 via-white to-amber-50/40 px-6 md:px-12 py-16" data-testid="leaderboard-section">
      <div className="max-w-6xl mx-auto">
        <div className="mb-4 flex items-center gap-3 text-[#8b6f3d]">
          <div className="h-px w-12 bg-[#8b6f3d]/40" />
          <span className="font-mono-data text-[11px] uppercase tracking-[0.3em]">
            {data?.mode === "top-visitors" ? T.tagFallback : T.tag}
          </span>
        </div>
        <h2 className="font-serif-mystery text-4xl md:text-5xl text-zinc-900 leading-[1.05] mb-3">
          {data?.mode === "top-visitors" ? (
            <>🌍 {T.titleFallback}</>
          ) : (
            <>🏆 {T.title}<br /><span className="italic text-[#8b6f3d]">{T.titleItalic}</span></>
          )}
        </h2>
        <p className="text-zinc-600 max-w-3xl mb-8">
          {data?.mode === "top-visitors" ? T.introFallback : T.intro}
        </p>

        {loading && <p className="text-zinc-500 text-center py-6">{T.loading}</p>}

        {data && data.top.length > 0 && (
          <div className="grid gap-2" data-testid="leaderboard-list">
            {data.top.map((e) => (
              <Link
                to={`/user/${e.username}`}
                key={e.user_id}
                data-testid={`leaderboard-entry-${e.rank}`}
                className={`flex items-center gap-4 p-4 rounded-2xl border transition-all hover:shadow-lg hover:-translate-y-0.5
                  ${e.is_admin
                    ? "border-[#8b6f3d] bg-gradient-to-r from-amber-100/80 to-white shadow"
                    : "border-zinc-200 bg-white hover:border-[#8b6f3d]/50"
                  }`}
              >
                <div className={`w-12 h-12 flex items-center justify-center font-serif-mystery text-xl rounded-full shrink-0
                  ${e.rank === 1 ? "bg-[#8b6f3d] text-white" : "bg-zinc-100 text-zinc-700"}`}>
                  {e.rank === 1 ? <Crown size={20} /> : `#${e.rank}`}
                </div>
                {e.picture ? (
                  <img src={e.picture} alt={e.username} className="w-12 h-12 rounded-full object-cover border-2 border-white shadow" />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-zinc-200 flex items-center justify-center text-zinc-500 font-medium">
                    {e.username?.[0]?.toUpperCase() || "?"}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-zinc-900 truncate">{e.username}</span>
                    {e.is_admin && <span className="text-[10px] font-mono-data uppercase tracking-widest text-[#8b6f3d]">{T.creator}</span>}
                    {e.custom_badges?.slice(0, 3).map((b, i) => (
                      <span key={i} className="text-lg" title={b[lang]}>{b.icon}</span>
                    ))}
                  </div>
                  {e.country && (
                    <div className="flex items-center gap-1 text-xs text-zinc-500 mt-0.5">
                      <MapPin size={11} /> {e.country}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-4 shrink-0">
                  <div className="text-center">
                    <div className="font-serif-mystery text-lg text-[#8b6f3d]">{e.badges_count}</div>
                    <div className="font-mono-data text-[9px] uppercase tracking-widest text-zinc-500">{T.badges}</div>
                  </div>
                  <div className="text-center hidden sm:block">
                    <div className="font-serif-mystery text-lg text-zinc-900">{e.visits}</div>
                    <div className="font-mono-data text-[9px] uppercase tracking-widest text-zinc-500">{T.visits}</div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}

        <div className="mt-6 flex items-center gap-2 text-xs text-zinc-500">
          <Sparkles size={12} className="text-[#8b6f3d]" />
          {T.footnote}
        </div>
      </div>
    </section>
  );
}

const FR = {
  tag: "Classement mondial",
  tagFallback: "Communauté",
  title: "Top 10 des Aventuriers",
  titleItalic: "du monde",
  titleFallback: "Plus grands explorateurs (pour le moment)",
  intro: "Les utilisateurs gagnent des badges en visitant les lieux répertoriés sur la carte. Plus vous visitez de lieux validés par GPS, plus vous débloquez de badges et montez dans le classement mondial. Explorez, découvrez, gagnez votre place !",
  introFallback: "Le classement officiel apparaîtra dès qu'un aventurier obtiendra son premier badge. En attendant, découvrez les explorateurs les plus actifs de la communauté WorldMysteriesMap.",
  loading: "Chargement du classement…",
  creator: "Créateur",
  badges: "Badges",
  visits: "Lieux",
  footnote: "Le classement est mis à jour automatiquement chaque jour.",
};
const EN = {
  tag: "World ranking",
  tagFallback: "Community",
  title: "Top 10 Adventurers",
  titleItalic: "worldwide",
  titleFallback: "Top explorers (so far)",
  intro: "Users earn badges by visiting locations mapped on the site. The more places you check-in via GPS, the more badges you unlock and the higher you rank worldwide. Explore, discover, earn your spot!",
  introFallback: "The official ranking will appear as soon as an adventurer earns their first badge. Meanwhile, discover the most active explorers of the WorldMysteriesMap community.",
  loading: "Loading ranking…",
  creator: "Creator",
  badges: "Badges",
  visits: "Places",
  footnote: "Ranking updated automatically every day.",
};
